import colors from '../theme/colors';

const styles = {
  page: {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: colors.brand.medium,
    color: colors.background.paper,
    position: 'relative',
    overflowX: 'hidden',
  },
  limitWidth: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: '0 24px',
    overflow: 'hidden', // Added to contain overflowing children
    boxSizing: 'border-box', // Ensure padding is included in width
  },
  launchBanner: {
    width: '100%',
    backgroundColor: colors.secondary.main,
    color: colors.brand.dark,
    padding: '12px 0',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: { xs: '14px', md: '16px' },
  },
  navigation: {
    position: 'relative',
    padding: '12px 0',
    width: '100%',
    logoContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      fontFamily: 'RooneySans',
      text: {
        fontSize: { xs: '24px', md: '28px' }, // Adjusted for mobile
        fontWeight: 'bold',
        color: colors.secondary.main,
        marginRight: '4px',
      },
      poweredBy: {
        fontSize: { xs: '10px', md: '14px' }, // Further adjusted for mobile
        color: colors.background.paper,
        marginLeft: { xs: '6px', md: '4px' }, // Further adjusted for mobile
        fontWeight: 'normal',
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap', // Prevent wrapping
      },
      alphaBannerContainer: {
        display: 'flex',
        justifyContent: 'center',
        transform: 'translate(0, -14px)',
      },
      alphaBanner: {
        backgroundColor: colors.primary.main,
        color: colors.background.paper,
        fontSize: '12px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: '4px',
        letterSpacing: '1px',
      },
      roundLogo: {
        width: '30px',
        marginRight: '8px',
      },
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      position: 'absolute',
      right: { xs: '12px', md: '24px' }, // Adjusted for mobile
      top: '50%',
      transform: 'translateY(-50%)',
      gap: { xs: '4px', md: '8px' }, // Added gap for spacing
    },
  },
  navContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    minHeight: { xs: '60px', md: '70px' },
  },
  header: {
    textAlign: 'center',
    color: colors.background.paper,
    background: `linear-gradient(180deg, ${colors.brand.medium}, ${colors.brand.darker})`,
    padding: { xs: '40px 24px', md: '60px 24px' },
    fontFamily: 'RooneySans',
    h1: {
      fontWeight: 800,
      fontSize: { xs: '38px', md: '56px' },
      lineHeight: 1.1,
      letterSpacing: '-1.5px',
      margin: '0 auto',
      maxWidth: '1000px',
      fontFamily: 'RooneySans',
      span: {
        color: colors.secondary.main,
        fontSize: 'inherit',
        fontWeight: 800,
        lineHeight: 1.1,
        fontFamily: 'RooneySans',
      },
    },
    h2: {
      fontWeight: 400,
      fontSize: { xs: '20px', md: '24px' },
      letterSpacing: '-0.6px',
      margin: '25px 0',
      fontFamily: 'RooneySans',
      '&.limited-width': {
        maxWidth: '700px',
        margin: '30px auto',
      },
      span: {
        color: colors.secondary.main,
        fontSize: 'inherit',
        fontWeight: 400,
        lineHeight: 1,
        fontFamily: 'RooneySans',
      },
    },
    '&.pricing-section': {
      padding: { xs: '60px 24px', md: '100px 24px' },
    },
  },

  heroSection: {
    padding: { xs: '40px 0', md: '60px 0' },
    fontFamily: 'RooneySans',
    color: colors.background.paper,
    title: {
      fontFamily: 'RooneySans',
      fontWeight: 700,
      fontSize: { xs: '32px', sm: '36px', md: '42px' },
      lineHeight: 1.2,
      letterSpacing: '-1px',
      color: colors.background.paper,
      mb: { xs: 2, md: 3 },
      span: {
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        color: colors.secondary.main,
      },
    },
    videoIframe: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: '8px',
    },
    subtext: {
      fontFamily: 'RooneySans',
      fontSize: { xs: '14px', md: '16px' },
      color: colors.grey.material400,
      mt: 1.5,
    },
  },

  videoPlaceholder: {
    position: 'relative',
    paddingBottom: '56.25%',
    height: 0,
    overflow: 'hidden',
    width: '100%',
    backgroundColor: colors.blackAlpha[20],
    borderRadius: '8px',
    boxSizing: 'border-box',
    boxShadow: `0 4px 20px ${colors.blackAlpha[20]}`,
  },

  subHeadingBelowHero: {
    fontFamily: 'RooneySans',
    fontWeight: 400,
    fontSize: { xs: '18px', md: '22px' },
    lineHeight: 1.5,
    color: colors.background.paper,
    maxWidth: '800px',
    margin: '0 auto',
    opacity: 0.9,
    span: {
      fontFamily: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      color: colors.secondary.main,
    },
  },

  actionVideoSection: {
    fontFamily: 'RooneySans',
    padding: { xs: '40px 0', md: '60px 0' },
    backgroundColor: colors.brand.dark,
    title: {
      fontFamily: 'RooneySans',
      fontWeight: 600,
      color: colors.background.paper,
      textAlign: 'center',
      fontSize: { xs: '24px', md: '30px' },
      margin: '0 auto 30px',
      maxWidth: '800px',
    },
    videoContainer: {
      position: 'relative',
      paddingBottom: '56.25%',
      height: 0,
      overflow: 'hidden',
      width: '100%', // Ensure it respects parent width
      maxWidth: '800px',
      margin: '0 auto',
      borderRadius: '8px',
      boxShadow: `0 6px 25px ${colors.shadows.yellow}`,
    },
    iframe: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
    },
  },

  testimonialSection: {
    fontFamily: 'RooneySans',
    padding: { xs: '50px 24px', md: '70px 24px' },
    paddingBottom: { xs: '20px', md: '20px' },
    textAlign: 'center',
    backgroundColor: colors.brand.medium,
    color: colors.background.paper,
    quote: {
      fontFamily: 'RooneySans',
      fontSize: { xs: '20px', md: '24px' },
      fontWeight: '500',
      fontStyle: 'italic',
      lineHeight: 1.6,
      maxWidth: '750px',
      margin: '0 auto 8px auto',
      color: colors.background.light,
    },
    author: {
      fontFamily: 'RooneySans',
      paddingBottom: '25px',
      fontSize: { xs: '16px', md: '18px' },
      fontStyle: 'normal',
      color: colors.secondary.main,
      fontWeight: '600',
    },
  },

  whyChooseSection: {
    fontFamily: 'RooneySans',
    padding: { xs: '40px 0 60px', md: '60px 0 80px' },
    backgroundColor: colors.brand.dark,
    title: {
      fontFamily: 'RooneySans',
      fontSize: { xs: '28px', md: '36px' },
      fontWeight: 600,
      color: colors.background.paper,
      marginBottom: '30px',
      textAlign: 'center',
      span: {
        fontFamily: 'RooneySans',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        color: colors.secondary.main,
      },
    },
    list: {
      listStyle: 'none',
      paddingLeft: 0,
      margin: '0 auto',
      maxWidth: '700px',
      listItem: {
        fontFamily: 'RooneySans',
        fontSize: { xs: '16px', md: '18px' },
        color: colors.background.paper,
        marginBottom: '20px',
        lineHeight: 1.7,
        display: 'flex',
        alignItems: 'flex-start',
        textAlign: 'left',
      },
      textSpan: {
        // Added style for the text span
        flexShrink: 1, // Allow shrinking if space is tight
        overflowWrap: 'break-word', // Break long words
        wordWrap: 'break-word', // Older browsers
        hyphens: 'auto', // Optional: allow hyphenation
      },
      checkmark: {
        color: colors.primary.main,
        marginRight: '12px',
        fontSize: { xs: '22px', md: '24px' },
      },
    },
  },

  sectionHeader: {
    textAlign: 'left',
    margin: '20px 0',
    color: colors.secondary.main,
    fontWeight: 600,
    fontSize: '24px',
    fontFamily: 'RooneySans',
  },
  button: {
    margin: '8px',
    borderRadius: '20px',
    textTransform: 'none',
    padding: '8px 24px',
    height: '40px',
    fontFamily: 'RooneySans',
    '&.page-button': {
      width: { xs: 'calc(100% - 40px)', md: 'auto' },
      fontSize: '17px',
    },
    login: {
      fontFamily: 'RooneySans',
      backgroundColor: colors.secondary.main,
      color: colors.secondary.contrast,
      '&:hover': {
        backgroundColor: colors.secondary.dark,
      },
    },
    signup: {
      fontFamily: 'RooneySans',
      backgroundColor: colors.primary.main,
      '&:hover': {
        backgroundColor: colors.primary.dark,
      },
    },
    largeSignup: {
      padding: '16px 48px',
      height: '60px',
      fontSize: '22px',
      fontWeight: '600',
    },
    topLogin: {
      padding: { xs: '4px 10px', md: '6px 20px' }, // Adjusted for mobile
      height: '36px',
      minWidth: { xs: '60px', md: '80px' }, // Adjusted for mobile
      borderRadius: '20px',
      fontSize: { xs: '13px', md: '16px' }, // Adjusted for mobile
    },
    topSignup: {
      padding: { xs: '4px 10px', md: '6px 20px' }, // Adjusted for mobile
      height: '36px',
      minWidth: { xs: '70px', md: '80px' }, // Adjusted for mobile, slightly more for "Sign Up"
      borderRadius: '20px',
      fontSize: { xs: '13px', md: '16px' }, // Adjusted for mobile
      whiteSpace: 'nowrap',
    },
  },
  works: {
    fontFamily: 'RooneySans',
    padding: { xs: '40px 0 60px', md: '60px 0 80px' },
    title: {
      fontFamily: 'RooneySans',
      fontWeight: 600,
      color: colors.secondary.main,
      textAlign: 'center',
      fontSize: { xs: '28px', md: '32px' },
      margin: '0 0 40px',
    },
  },
  howItWorksIcons: {
    display: 'flex',
    justifyContent: 'center',
  },
  unlock: {
    fontFamily: 'RooneySans',
    padding: { xs: '40px 0 60px', md: '60px 0 80px' },
    title: {
      fontFamily: 'RooneySans',
      fontSize: { xs: '28px', md: '40px' },
      fontWeight: 600,
      marginBottom: '40px',
      textAlign: 'center',
      maxWidth: '800px',
      margin: '0 auto 40px',
      span: {
        fontFamily: 'RooneySans',
        fontSize: 'inherit',
        fontWeight: 600,
        color: colors.secondary.main,
      },
    },
    textSection: {
      fontFamily: 'RooneySans',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%',
      title: {
        fontFamily: 'RooneySans',
        fontSize: { xs: '28px', md: '36px' },
        fontWeight: 600,
        color: colors.secondary.main,
        maxWidth: '500px',
        marginBottom: '16px',
        '&.orange': {
          color: colors.orange.brand,
        },
        '&.blue': {
          color: colors.primary.main,
        },
      },
      subtitle: {
        fontFamily: 'RooneySans',
        fontSize: { xs: '18px', md: '22px' },
        fontWeight: 500,
        color: colors.background.paper,
        maxWidth: '500px',
      },
    },
    image: {
      width: '100%',
      height: 'auto',
      maxWidth: { xs: '320px', sm: '400px', md: '500px' }, // Adjusted for mobile
      display: 'block',
      margin: '0 auto',
    },
    rowReverse: {
      flexDirection: { xs: 'column-reverse', md: 'row' },
    },
  },
  icon: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '0 16px',
    img: {
      marginBottom: '20px',
    },
    title: {
      fontFamily: 'RooneySans',
      fontSize: { xs: '18px', md: '20px' },
      fontWeight: 600,
      color: colors.secondary.main,
      margin: '0 0 12px',
    },
    description: {
      fontFamily: 'RooneySans',
      fontSize: { xs: '14px', md: '16px' },
      lineHeight: 1.6,
      fontWeight: 500,
      maxWidth: '90%',
      margin: '0 auto',
    },
  },
  faq: {
    padding: '60px 0',
    heading: {
      fontFamily: 'RooneySans',
      color: colors.background.paper,
      fontWeight: 600,
      fontSize: '22px',
    },
    title: {
      fontFamily: 'RooneySans',
      color: colors.secondary.main,
      fontSize: '20px',
      margin: '30px 0 20px',
      fontWeight: 600,
    },
    subtitle: {
      fontFamily: 'RooneySans',
      color: colors.background.paper,
    },
  },
  imageSection: {
    backgroundColor: colors.brand.dark,
    padding: '40px 0',
  },
  helpSupport: {
    back: {},
    container: {
      padding: '100px 20px 80px',
      minHeight: 'calc(100vh - 70px)',
    },
    heading: {
      fontFamily: 'RooneySans',
      fontSize: '40px',
      fontWeight: 600,
      color: colors.secondary.main,
      marginBottom: '16px',
    },
    title: {
      fontFamily: 'RooneySans',
      fontSize: '32px',
      fontWeight: 600,
      color: colors.background.paper,
      marginBottom: '16px',
    },
    subtitle: {
      fontFamily: 'RooneySans',
      fontSize: '20px',
      fontWeight: 600,
      color: colors.secondary.main,
      marginBottom: '16px',
    },
    list: {
      paddingLeft: '20px',
      marginBottom: '16px',
      listStyleType: 'none',
    },
    listItem: {
      alignItems: 'flex-start',
      marginBottom: '10px',
      padding: 0,
      '&::before': {
        content: '"\\2022"',
        color: colors.secondary.main,
        fontWeight: 'bold',
        flexShrink: 0,
        width: '1.5em',

        textAlign: 'center',
      },

      '& .listItemText': {
        fontFamily: 'RooneySans',
        fontSize: '16px',
        color: colors.background.paper,
        flexGrow: 1,
      },
    },
    paragraph: {
      fontFamily: 'RooneySans',
      fontSize: '16px',
      color: colors.background.paper,
      marginBottom: '16px',
      '& .MuiListItem-root &': {
        marginBottom: 0,
      },
    },
    contact: {
      fontFamily: 'RooneySans',
      color: colors.secondary.main,
    },
    link: {
      color: colors.secondary.main,
      fontFamily: 'RooneySans',
      fontWeight: 'bold',
      '&:hover': {
        filter: 'brightness(1.2)',
      },
    },
  },
  sidebar: {
    main: {
      backgroundColor: colors.brand.medium,
      color: colors.background.paper,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      padding: '16px',
      position: 'relative',
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    },
    logoText: {
      fontFamily: 'RooneySans',
      fontSize: '28px',
      fontWeight: 'bold',
    },
    qsText: {
      color: colors.secondary.main,
    },
    aiText: {
      color: colors.primary.main,
    },
    poweredByContainer: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '4px',
    },
    poweredByText: {
      fontSize: '14px',
      color: colors.background.paper,
      fontWeight: 'normal',
      marginRight: '8px',
    },
    alphaBadge: {
      backgroundColor: colors.primary.main,
      color: colors.background.paper,
      fontSize: '12px',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: '4px',
      letterSpacing: '1px',
    },
    roundLogo: {
      width: '30px',
      marginRight: '8px',
    },
    nav: {},
    navItem: {
      '&.active': {},
    },
    navIcon: {},
    feedbackButton: {},
  },
  termsSection: {
    padding: '30px 24px 40px',
    textAlign: 'center',
    fontFamily: 'RooneySans',
    fontSize: { xs: '16px', md: '18px' },
    color: colors.background.paper,
    link: {
      color: colors.secondary.main,
      textDecoration: 'underline',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      '&:hover': {
        filter: 'brightness(1.2)',
      },
    },
  },
};

export default styles;
